import React, { useState, useEffect } from 'react';
import { Sidebar } from './components/Sidebar';
import { TopBar } from './components/TopBar';
import { Dashboard } from './components/Dashboard';
import { TourFilesList } from './components/TourFilesList';
import { TourFileDetail } from './components/TourFileDetail';
import { MovementChart } from './components/MovementChart';
import { EmailConversations } from './components/EmailConversations';
import { BookingsPage } from './components/BookingsPage';
import { BookingDetail } from './components/BookingDetail';
import { TicketDetail } from './components/TicketDetail';
import { ConversationCenter } from './components/ConversationCenter';
import { SettingsPage } from './components/SettingsPage';
import { LoginScreen } from './components/LoginScreen';
import { SeriesTemplatesPage } from './components/pages/SeriesTemplatesPage';

export type UserRole = 'admin' | 'manager';

export interface User {
  id?: string;
  name: string;
  role: UserRole;
  isAdmin: boolean;
  email?: string;
  assignedOperators?: string[];
  managerId?: string | null;
}

export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [selectedTourId, setSelectedTourId] = useState<string | null>(null);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User>({
    name: 'Madhu Chaudhary',
    role: 'admin',
    isAdmin: true
  });

  // Check for Google OAuth callback on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const googleAuth = urlParams.get('googleAuth');

    if (token) {
      // Decode JWT to get user info
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        
        // Store token
        localStorage.setItem('authToken', token);
        localStorage.setItem('token', token);

        // Set user from token
        // Fetch full user profile for assignedOperators
        const fetchProfile = async () => {
          try {
            const res = await fetch('/api/auth/me', {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const profileData = await res.json();
            if (profileData.success && profileData.data?.user) {
              const u = profileData.data.user;
              setCurrentUser(prev => ({
                ...prev,
                assignedOperators: u.assignedOperators ? (typeof u.assignedOperators === 'string' ? JSON.parse(u.assignedOperators) : u.assignedOperators) : [],
                managerId: u.managerId || null
              }));
            }
          } catch (e) { console.error('Failed to fetch profile:', e); }
        };
        setCurrentUser({
          id: payload.id,
          name: payload.name || payload.email.split('@')[0],
          email: payload.email,
          role: payload.role === 'ADMIN' ? 'admin' : 'manager',
          isAdmin: payload.role === 'ADMIN'
        });
        fetchProfile();
        
        setIsLoggedIn(true);

        // Clean URL
        window.history.replaceState({}, document.title, '/');
      } catch (error) {
        console.error('Failed to decode token:', error);
      }
    } else if (googleAuth === 'error') {
      alert('Google authentication failed. Please try again.');
      window.history.replaceState({}, document.title, '/');
    }
  }, []);

  if (!isLoggedIn) {
    return <LoginScreen onLogin={(user) => {
      setCurrentUser(user);
      setIsLoggedIn(true);
    }} />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard user={currentUser} />;
      case 'tour-files':
        return <TourFilesList onSelectTour={(id) => {
          setSelectedTourId(id);
          setCurrentPage('tour-detail');
        }} currentUser={currentUser} onNavigate={setCurrentPage} />;
      case 'movement-chart':
        return <MovementChart />;
      case 'tour-detail':
        if (!selectedTourId) {
          setCurrentPage('tour-files');
          return <TourFilesList onSelectTour={(id) => {
            setSelectedTourId(id);
            setCurrentPage('tour-detail');
          }} currentUser={currentUser} onNavigate={setCurrentPage} />;
        }
        return <TourFileDetail tourId={selectedTourId} onBack={() => {
          setSelectedTourId(null);
          setCurrentPage('tour-files');
        }} currentUser={currentUser} />;
      case 'emails':
        return <EmailConversations />;
      case 'bookings':
        return <BookingsPage onSelectBooking={(id) => {
          setSelectedBookingId(id);
          setCurrentPage('booking-detail');
        }} />;
      case 'booking-detail':
        return <BookingDetail bookingId={selectedBookingId} onBack={() => setCurrentPage('bookings')} />;
      case 'ticket-detail':
        return <TicketDetail ticketId={selectedTicketId} onBack={() => setCurrentPage('bookings')} />;
      case 'conversations':
        return <ConversationCenter />;
      case 'configuration':
        return <SeriesTemplatesPage />;
      case 'settings':
        return <SettingsPage currentUser={currentUser} />;
      default:
        return <Dashboard user={currentUser} />;
    }
  };

  const handleLogout = () => {
    // Clear JWT tokens
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    
    setIsLoggedIn(false);
    setCurrentPage('dashboard');
    setSelectedTourId(null);
    setSelectedBookingId(null);
    setSelectedTicketId(null);
    setCurrentUser({
      name: 'Madhu Chaudhary',
      role: 'admin',
      isAdmin: true
    });
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar currentPage={currentPage} onNavigate={setCurrentPage} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar currentUser={currentUser} onLogout={handleLogout} />
        <main className="flex-1 overflow-y-auto">
          {renderPage()}
        </main>
      </div>
    </div>
  );
}